import axios from 'axios';
import { getLibraryLoggingAttributes, getLogger, Dispenser } from '@vp/digital-logging-lib';
import axiosRetry from 'axios-retry';
import { setupCache } from 'axios-cache-adapter';
import { getIdentity } from '@vp/digital-auth-lib';

var RetryPolicy;
(function (RetryPolicy) {
    RetryPolicy[RetryPolicy["None"] = 0] = "None";
    RetryPolicy[RetryPolicy["Immediate"] = 1] = "Immediate";
    RetryPolicy[RetryPolicy["LinearBackOff"] = 2] = "LinearBackOff";
    RetryPolicy[RetryPolicy["ExponentialBackOff"] = 3] = "ExponentialBackOff";
})(RetryPolicy || (RetryPolicy = {}));

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    if (typeof b !== "function" && b !== null)
        throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var name = "@vp/digital-http-client-lib";
var version = "0.19.21";

var getLibLogger = function (attributes) {
    var attr = getLibraryLoggingAttributes(name, version, attributes);
    return getLogger(attr);
};

var MISSING_URL = '(missing)';
var NOT_RECEIVED = 'Response not received';
function axiosRequestLogger(axiosRequestConfig) {
    var _a, _b;
    getLibLogger().debug('Http request initiated', {
        baseURL: (_a = axiosRequestConfig.baseURL) !== null && _a !== void 0 ? _a : MISSING_URL,
        url: (_b = axiosRequestConfig.url) !== null && _b !== void 0 ? _b : MISSING_URL,
    });
    return axiosRequestConfig;
}
function axiosResponseLogger(axiosResponse) {
    var _a, _b, _c, _d, _e;
    var fromCache = axiosResponse.request.fromCache === true;
    getLibLogger().debug('Http response received', {
        baseURL: (_b = (_a = axiosResponse.config) === null || _a === void 0 ? void 0 : _a.baseURL) !== null && _b !== void 0 ? _b : MISSING_URL,
        url: (_d = (_c = axiosResponse.config) === null || _c === void 0 ? void 0 : _c.url) !== null && _d !== void 0 ? _d : MISSING_URL,
        status: (_e = axiosResponse.status) !== null && _e !== void 0 ? _e : NOT_RECEIVED,
        fromCache: fromCache,
    });
    return axiosResponse;
}
function axiosErrorLogger(error) {
    var _a, _b, _c, _d;
    getLibLogger().warn('Http error encountered', {
        baseURL: (_a = error.config) === null || _a === void 0 ? void 0 : _a.baseURL,
        url: (_b = error.config) === null || _b === void 0 ? void 0 : _b.url,
        status: (_d = (_c = error.response) === null || _c === void 0 ? void 0 : _c.status) !== null && _d !== void 0 ? _d : 'Response not received',
        message: error.message,
    });
    return Promise.reject(error);
}
function addAxiosLogger(axiosInstance) {
    axiosInstance.interceptors.request.use(axiosRequestLogger);
    axiosInstance.interceptors.response.use(axiosResponseLogger, axiosErrorLogger);
}

var _a;
var addRandom = function (base, randomness) { return base + Math.trunc(Math.random() * randomness * base); };
var delayCalculators = (_a = {},
    _a[RetryPolicy.None] = null,
    _a[RetryPolicy.Immediate] = function (retryCount, retryOffsetMS, retryRandomness) {
        return addRandom(retryOffsetMS, retryRandomness);
    },
    _a[RetryPolicy.LinearBackOff] = function (retryCount, retryOffsetMS, retryRandomness) {
        return addRandom(retryCount * retryOffsetMS, retryRandomness);
    },
    _a[RetryPolicy.ExponentialBackOff] = function (retryCount, retryOffsetMS, retryRandomness) { return addRandom(Math.pow(2, (retryCount - 1)) * retryOffsetMS, retryRandomness); },
    _a);
function addAxiosRetry(axiosInstance, retryConfig) {
    var calculateDelay = delayCalculators[retryConfig.policy];
    if (calculateDelay) {
        var retryDelay = function (retryCount, error) {
            var _a, _b;
            var delay = calculateDelay(retryCount, retryConfig.offsetMS, retryConfig.randomness);
            getLibLogger().warn('Retrying Http request', {
                baseURL: (_a = error.config) === null || _a === void 0 ? void 0 : _a.baseURL,
                url: (_b = error.config) === null || _b === void 0 ? void 0 : _b.url,
                status: error.response.status,
                retryCount: retryCount,
                delay: delay,
            });
            return delay;
        };
        axiosRetry(axiosInstance, {
            retries: retryConfig.count,
            shouldResetTimeout: retryConfig.shouldResetTimeout,
            retryDelay: retryDelay,
        });
    }
}

function makeCache(cacheConfig) {
    var disableCache = cacheConfig.maxSize === 0 || cacheConfig.maxAgeMS === 0;
    if (disableCache) {
        return null;
    }
    var cache = setupCache({
        limit: cacheConfig.maxSize,
        maxAge: cacheConfig.maxAgeMS,
        exclude: {
            query: false,
        },
    });
    return cache;
}

var DEFAULT_TIMEOUT_MS = 2 * 1000;
var DEFAULT_RETRY_POLICY = RetryPolicy.None;
var DEFAULT_RETRY_COUNT = 0;
var DEFAULT_RETRY_OFFSET_MS = 0;
var DEFAULT_RETRY_RANDOMNESS_PERCENT = 0.2;
var DEFAULT_CACHE_MAX_SIZE = 0;
var DEFAULT_CACHE_MAX_AGE_MS = 0;
var DEFAULT_RETRY_RESET_TIMEOUT = true;
function makeFinalOpConfig(apiConfig, opConfig) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3, _4, _5;
    return {
        timeoutMS: (_b = (_a = opConfig.timeoutMS) !== null && _a !== void 0 ? _a : apiConfig.defaultTimeoutMS) !== null && _b !== void 0 ? _b : DEFAULT_TIMEOUT_MS,
        retry: {
            policy: (_f = (_d = (_c = opConfig.retry) === null || _c === void 0 ? void 0 : _c.policy) !== null && _d !== void 0 ? _d : (_e = apiConfig.defaultRetry) === null || _e === void 0 ? void 0 : _e.policy) !== null && _f !== void 0 ? _f : DEFAULT_RETRY_POLICY,
            count: (_k = (_h = (_g = opConfig.retry) === null || _g === void 0 ? void 0 : _g.count) !== null && _h !== void 0 ? _h : (_j = apiConfig.defaultRetry) === null || _j === void 0 ? void 0 : _j.count) !== null && _k !== void 0 ? _k : DEFAULT_RETRY_COUNT,
            offsetMS: (_p = (_m = (_l = opConfig.retry) === null || _l === void 0 ? void 0 : _l.offsetMS) !== null && _m !== void 0 ? _m : (_o = apiConfig.defaultRetry) === null || _o === void 0 ? void 0 : _o.offsetMS) !== null && _p !== void 0 ? _p : DEFAULT_RETRY_OFFSET_MS,
            randomness: (_t = (_r = (_q = opConfig.retry) === null || _q === void 0 ? void 0 : _q.randomness) !== null && _r !== void 0 ? _r : (_s = apiConfig.defaultRetry) === null || _s === void 0 ? void 0 : _s.randomness) !== null && _t !== void 0 ? _t : DEFAULT_RETRY_RANDOMNESS_PERCENT,
            shouldResetTimeout: (_x = (_v = (_u = opConfig.retry) === null || _u === void 0 ? void 0 : _u.shouldResetTimeout) !== null && _v !== void 0 ? _v : (_w = apiConfig.defaultRetry) === null || _w === void 0 ? void 0 : _w.shouldResetTimeout) !== null && _x !== void 0 ? _x : DEFAULT_RETRY_RESET_TIMEOUT,
        },
        cache: {
            maxSize: (_1 = (_z = (_y = opConfig.cache) === null || _y === void 0 ? void 0 : _y.maxSize) !== null && _z !== void 0 ? _z : (_0 = apiConfig.defaultCache) === null || _0 === void 0 ? void 0 : _0.maxSize) !== null && _1 !== void 0 ? _1 : DEFAULT_CACHE_MAX_SIZE,
            maxAgeMS: (_5 = (_3 = (_2 = opConfig.cache) === null || _2 === void 0 ? void 0 : _2.maxAgeMS) !== null && _3 !== void 0 ? _3 : (_4 = apiConfig.defaultCache) === null || _4 === void 0 ? void 0 : _4.maxAgeMS) !== null && _5 !== void 0 ? _5 : DEFAULT_CACHE_MAX_AGE_MS,
        },
    };
}

function axiosAuth(axiosRequestConfig) {
    var accessToken = getIdentity().accessToken;
    if (!accessToken) {
        getLibLogger().warn('No accessToken provided by auth');
        throw new Error('No accessToken provided by auth');
    }
    axiosRequestConfig.headers.Authorization = "Bearer ".concat(accessToken);
    getLibLogger().debug('Http Authorization header added', axiosRequestConfig);
    return axiosRequestConfig;
}
function addAxiosAuth(axiosInstance) {
    axiosInstance.interceptors.request.use(axiosAuth);
}

function makeAxiosConfig(apiConfig, opConfig, cache, config) {
    var axiosConfig = __assign({ baseURL: apiConfig.baseUrl, timeout: opConfig.timeoutMS }, config);
    if (cache)
        axiosConfig.cache = cache;
    return axiosConfig;
}
function makeHttpClient(apiConfig, opConfig, requiresAuth, config) {
    if (requiresAuth === void 0) { requiresAuth = false; }
    var finalOpConfig = makeFinalOpConfig(apiConfig, opConfig);
    var cache = makeCache(finalOpConfig.cache);
    var axiosConfig = makeAxiosConfig(apiConfig, finalOpConfig, cache, config);
    getLibLogger().debug('Creating Axios Http Client', {
        apiConfig: apiConfig,
        opConfig: opConfig,
        finalOpConfig: finalOpConfig,
        axiosConfig: axiosConfig,
    });
    var axiosInstance = axios.create(axiosConfig);
    addAxiosLogger(axiosInstance);
    if (requiresAuth) {
        addAxiosAuth(axiosInstance);
    }
    addAxiosRetry(axiosInstance, finalOpConfig.retry);
    axiosInstance.interceptors.request.use(function (config) {
        config.headers['x-correlation-id'] = apiConfig.correlationId;
        return config;
    });
    return axiosInstance;
}

var HttpClientDispenser = (function (_super) {
    __extends(HttpClientDispenser, _super);
    function HttpClientDispenser(apiConfig, opConfig, requiresAuth) {
        if (requiresAuth === void 0) { requiresAuth = false; }
        return _super.call(this, function () { return makeHttpClient(apiConfig, opConfig, requiresAuth); }) || this;
    }
    return HttpClientDispenser;
}(Dispenser));

function makeGraphqlClient(apiConfig, opConfig, requiresAuth, query) {
    if (requiresAuth === void 0) { requiresAuth = false; }
    var axiosConfig = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        data: query,
    };
    var graphqlClient = makeHttpClient(apiConfig, opConfig, requiresAuth, axiosConfig);
    var newTransformers = []
        .concat(graphqlClient.defaults.transformResponse)
        .concat(function (data) {
        if (!data)
            return;
        return data.data;
    });
    graphqlClient.defaults.transformResponse = newTransformers;
    return graphqlClient;
}

var GraphqlClientDispenser = (function (_super) {
    __extends(GraphqlClientDispenser, _super);
    function GraphqlClientDispenser(apiConfig, opConfig, requiresAuth, query) {
        if (requiresAuth === void 0) { requiresAuth = false; }
        return _super.call(this, function () { return makeGraphqlClient(apiConfig, opConfig, requiresAuth, query); }) || this;
    }
    return GraphqlClientDispenser;
}(Dispenser));

export { GraphqlClientDispenser, HttpClientDispenser, RetryPolicy, makeGraphqlClient, makeHttpClient };
